import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from '../hooks/useTranslation';
import { 
  PhoneCall, 
  Mail, 
  MapPin, 
  Clock, 
  Send, 
  CheckCircle, 
  Calendar, 
  User, 
  MessageCircle, 
  ChevronLeft, 
  ChevronRight,
  Facebook,
  Instagram,
  Youtube,
  Globe,
  Award,
  Shield,
  Heart
} from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SEOHead from '../components/SEO/SEOHead';
import BookingCalendar from '../components/ui/BookingCalendar';
import PhoneInput from '../components/ui/PhoneInput';
import { BRAND } from '../data/brand';

export default function ContactsPage() {
  const { t, currentLanguage } = useTranslation();
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+49',
    message: '',
    service: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch('/api/booking', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          selectedDate,
          selectedTime,
          language: currentLanguage
        }),
      });

      const result = await response.json();
      
      if (result.success) {
        setIsSubmitted(true);
      } else {
        console.error('Ошибка при отправке формы:', result.error);
        alert(result.error || 'Произошла ошибка при отправке формы');
      }
    } catch (error) {
      console.error('Ошибка сети:', error);
      alert('Ошибка сети. Пожалуйста, попробуйте еще раз.');
    } finally {
      setIsSubmitting(false);
    }
    
    // Сброс формы через 5 секунд
    setTimeout(() => {
      setIsSubmitted(false);
      setShowForm(false);
      setSelectedDate('');
      setSelectedTime('');
      setFormData({
        name: '',
        email: '',
        phone: '',
        countryCode: '+49',
        message: '',
        service: ''
      });
    }, 5000);
  };

  const handleDateTimeSelect = (date, time) => {
    setSelectedDate(date);
    setSelectedTime(time);
    setShowForm(true);
  };

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  const services = [
    'Консультация',
    'Самогипноз',
    'Снятие стресса',
    'Работа с фобиями',
    'Улучшение сна',
    'Повышение мотивации',
    'Другое'
  ];

  const contactInfo = [
    {
      icon: PhoneCall,
      title: t('contact.phone'),
      value: BRAND.phone,
      description: t('contact.call_hours')
    },
    {
      icon: Mail,
      title: t('contact.email'),
      value: BRAND.email,
      description: t('contact.response_time')
    },
    {
      icon: MapPin,
      title: t('contact.address'),
      value: BRAND.address,
      description: t('contact.consultation_types')
    },
    {
      icon: Clock,
      title: t('contact.work_hours'),
      value: t('contact.weekdays_hours'),
      description: t('contact.weekend_hours')
    }
  ];

  const socialLinks = [
    {
      icon: Facebook,
      name: 'Facebook',
      url: BRAND.social.facebook,
      color: 'text-blue-600'
    },
    {
      icon: Instagram,
      name: 'Instagram',
      url: BRAND.social.instagram,
      color: 'text-pink-600'
    },
    {
      icon: MessageCircle,
      name: 'WhatsApp',
      url: BRAND.social.whatsapp,
      color: 'text-green-600'
    }
  ];

  const guarantees = [
    {
      icon: Shield,
      title: t('contact.guarantees.privacy.title'),
      description: t('contact.guarantees.privacy.description')
    },
    {
      icon: Award,
      title: t('contact.guarantees.professionalism.title'),
      description: t('contact.guarantees.professionalism.description')
    },
    {
      icon: Heart,
      title: t('contact.guarantees.individual.title'),
      description: t('contact.guarantees.individual.description')
    }
  ];

  return (
    <div className="min-h-screen bg-white text-dark-900">
      <SEOHead 
        title={t('contact.seo.title')}
        description={t('contact.seo.description')}
        keywords={t('contact.seo.keywords')}
      />
      <Header />
      <main>
        {/* Контактная информация */}
        <section className="py-16 sm:py-20 bg-black">
          <div className="container-spacious">
            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="text-center mb-12 sm:mb-16"
            >
              <motion.h2 variants={item} className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6 text-white">
                {t('contact.ways.title')}
              </motion.h2>
              <motion.p variants={item} className="text-gray-300 max-w-3xl mx-auto text-sm sm:text-base">
                {t('contact.ways.subtitle')}
              </motion.p>
            </motion.div>

            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-12 sm:mb-16"
            >
              {contactInfo.map((info, index) => (
                <motion.div
                  key={index}
                  variants={item}
                  className="bg-white/5 border border-white/10 rounded-2xl p-4 sm:p-6 hover:bg-white/15 transition-all hover:scale-105 text-center"
                >
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-rose-100 rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <info.icon className="w-6 h-6 sm:w-8 sm:h-8 text-rose-600" />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold mb-2 text-white">{info.title}</h3>
                  <p className="text-rose-300 font-medium mb-2 text-sm sm:text-base">{info.value}</p>
                  <p className="text-gray-200 text-xs sm:text-sm">{info.description}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Запись на консультацию */}
        <section id="contact" className="py-16 sm:py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="text-center mb-8 sm:mb-12"
            >
              <motion.h2 variants={item} className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6 text-slate-800">
                {t('contact.consultation.title')}
              </motion.h2>
              <motion.p variants={item} className="text-slate-600 text-base sm:text-lg max-w-3xl mx-auto">
                {t('contact.consultation.description')}
              </motion.p>
            </motion.div>

            <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-start">
              
              {/* Левая колонка - календарь */}
              <motion.div
                variants={container}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="space-y-6"
              >
                <motion.div variants={item}>
                  <BookingCalendar onDateTimeSelect={handleDateTimeSelect} />
                </motion.div>
              </motion.div>

              {/* Правая колонка - форма и информация */}
              <motion.div
                variants={container}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="space-y-8"
              >
                {/* Сообщение когда время не выбрано */}
                {(!selectedDate || !selectedTime) && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-3xl shadow-xl p-6 sm:p-8 border border-slate-200 text-center"
                  >
                    <Calendar className="h-12 w-12 sm:h-16 sm:w-16 text-slate-400 mx-auto mb-4 sm:mb-6" />
                    <h3 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 text-slate-800">
                      {t('contact.consultation.title')}
                    </h3>
                    <p className="text-slate-600 mb-4 sm:mb-6 text-sm sm:text-base">
                      {t('contact.form_instructions')}
                    </p>
                    <div className="bg-rose-50 border border-rose-200 rounded-2xl p-3 sm:p-4">
                      <h4 className="font-medium text-rose-800 mb-2 text-sm sm:text-base">{t('contact.expectations.title')}</h4>
                      <ul className="text-xs sm:text-sm text-rose-700 space-y-1 text-left">
                        <li>• {t('contact.expectations.consultation')}</li>
                        <li>• {t('contact.expectations.discussion')}</li>
                        <li>• {t('contact.expectations.recommendations')}</li>
                        <li>• {t('contact.expectations.questions')}</li>
                      </ul>
                    </div>
                  </motion.div>
                )}

                {/* Выбранная дата и время */}
                {(selectedDate && selectedTime) && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-rose-50 border border-rose-200 rounded-2xl p-4 sm:p-6"
                  >
                    <h3 className="text-lg sm:text-xl font-semibold text-rose-800 mb-3 sm:mb-4 flex items-center gap-2 sm:gap-3">
                      <Calendar className="h-5 w-5 sm:h-6 sm:w-6" />
                      {t('contact.selected_time')}
                    </h3>
                    <div className="space-y-2">
                      <p className="text-rose-700 text-sm sm:text-base">
                        <span className="font-medium">{t('contact.date')}:</span> {selectedDate}
                      </p>
                      <p className="text-rose-700 text-sm sm:text-base">
                        <span className="font-medium">{t('contact.time')}:</span> {selectedTime}
                      </p>
                    </div>
                  </motion.div>
                )}

                {/* Форма записи */}
                {showForm && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-3xl shadow-xl p-6 sm:p-8 border border-slate-200"
                  >
                    <h3 className="text-lg sm:text-xl font-semibold mb-4 sm:mb-6 text-slate-800 flex items-center gap-2 sm:gap-3">
                      <User className="h-5 w-5 sm:h-6 sm:w-6 text-primary-600" />
                      {t('contact.your_data')}
                    </h3>
                    
                    {!isSubmitted ? (
                      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className='block text-sm font-medium text-slate-700 mb-2'>
                              {t('contact.form.name')} *
                            </label>
                            <input
                              type="text"
                              name="name"
                              required
                              value={formData.name}
                              onChange={handleInputChange}
                              className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm sm:text-base"
                              placeholder={t('contact.form.name_placeholder')}
                            />
                          </div>
                          <div>
                            <label className='block text-sm font-medium text-slate-700 mb-2'>
                              {t('contact.form.email')} *
                            </label>
                            <input
                              type="email"
                              name="email"
                              required
                              value={formData.email}
                              onChange={handleInputChange}
                              className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm sm:text-base"
                              placeholder={t('contact.form.email_placeholder')}
                            />
                          </div>
                        </div>
                        
                        <div>
                          <label className='block text-sm font-medium text-slate-700 mb-2'>
                            {t('contact.form.phone')}
                          </label>
                          <PhoneInput 
                            value={formData} 
                            onChange={handleInputChange}
                            placeholder={t('contact.form.phone_placeholder')}
                          />
                        </div>
                        
                        <div>
                          <label className='block text-sm font-medium text-slate-700 mb-2'>
                            {t('contact.form.message')}
                          </label>
                          <textarea
                            name="message"
                            rows={4}
                            value={formData.message}
                            onChange={handleInputChange}
                            className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm sm:text-base"
                            placeholder={t('contact.form.message_placeholder')}
                          />
                        </div>
                        
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="w-full bg-primary-600 hover:bg-primary-700 text-white py-3 sm:py-4 px-4 sm:px-6 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 sm:gap-3 text-sm sm:text-base"
                        >
                          {isSubmitting ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-white"></div>
                              {t('contact.form.submitting')}
                            </>
                          ) : (
                            <>
                              <Send className="h-4 w-4 sm:h-5 sm:w-5" />
                              {t('contact.form.submit')}
                            </>
                          )}
                        </button>
                      </form>
                    ) : (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="text-center py-6 sm:py-8"
                      >
                        <CheckCircle className="h-12 w-12 sm:h-16 sm:w-16 text-green-500 mx-auto mb-3 sm:mb-4" />
                        <h3 className="text-lg sm:text-xl font-semibold text-slate-800 mb-2">
                          {t('contact.form.success_title')}
                        </h3>
                        <p className="text-slate-600 text-sm sm:text-base">
                          {t('contact.form.success_message', { date: selectedDate, time: selectedTime })}
                        </p>
                      </motion.div>
                    )}
                  </motion.div>
                )}
              </motion.div>
            </div>
          </div>
        </section>

        {/* Контактная информация */}
        <section className="py-16 sm:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Заголовок секции */}
            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="text-center mb-12 sm:mb-16"
            >
              <motion.h2 variants={item} className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6 text-dark-900">
                {t('contact.info.title')}
              </motion.h2>
              <motion.p variants={item} className="text-gray-600 text-base sm:text-lg max-w-3xl mx-auto">
                {t('contact.info.subtitle')}
              </motion.p>
            </motion.div>

            <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-start">
              
              {/* Левая колонка - контактная информация */}
              <motion.div
                variants={container}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="space-y-6 sm:space-y-8"
              >
                {/* Основная контактная информация */}
                <motion.div variants={item} className="space-y-4 sm:space-y-6">
                  {contactInfo.map((info, index) => (
                    <div key={index} className="flex items-start gap-3 sm:gap-4 p-4 sm:p-6 bg-white rounded-2xl hover:bg-gray-100 transition-all duration-200 shadow-sm border border-gray-200">
                      <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-rose-100 rounded-xl flex items-center justify-center">
                        <info.icon className="h-5 w-5 sm:h-6 sm:w-6 text-rose-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-dark-900 mb-1 text-sm sm:text-base">{info.title}</h3>
                        <p className="text-rose-600 hover:text-rose-700 font-medium text-sm sm:text-base">{info.value}</p>
                        <p className="text-gray-600 text-xs sm:text-sm mt-1">{info.description}</p>
                      </div>
                    </div>
                  ))}
                </motion.div>

                {/* Социальные сети */}
                <motion.div variants={item} className="bg-white rounded-2xl p-4 sm:p-6 border border-gray-200 shadow-sm">
                  <h3 className="font-semibold text-dark-900 mb-3 sm:mb-4 flex items-center gap-2 sm:gap-3 text-sm sm:text-base">
                    <Globe className="h-4 w-4 sm:h-5 sm:w-5 text-rose-600" />
                    {t('contact.social.title')}
                  </h3>
                  <div className="flex flex-wrap gap-2 sm:gap-3">
                    {socialLinks.map((social, index) => (
                      <a
                        key={index}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`p-2 sm:p-3 rounded-xl transition-all duration-200 flex items-center justify-center ${
                          social.name === 'Facebook' ? 'bg-blue-600 hover:bg-blue-700 text-white' :
                          social.name === 'Instagram' ? 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white' :
                          social.name === 'WhatsApp' ? 'bg-green-500 hover:bg-green-600 text-white' :
                          'bg-gray-600 hover:bg-gray-700 text-white'
                        }`}
                        title={social.name}
                      >
                        <social.icon className="h-4 w-4 sm:h-5 sm:w-5" />
                      </a>
                    ))}
                  </div>
                </motion.div>
              </motion.div>

              {/* Правая колонка - Google карта */}
              <motion.div
                variants={container}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="space-y-4 sm:space-y-6"
              >
                {/* Google карта */}
                <motion.div variants={item} className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-200">
                  <div className="p-4 sm:p-6 border-b border-gray-200">
                    <h3 className="text-base sm:text-lg font-semibold text-dark-900 mb-2 flex items-center gap-2 sm:gap-3">
                      <MapPin className="h-5 w-5 sm:h-6 sm:w-6 text-rose-600" />
                      {t('contact.office')}
                    </h3>
                    <p className="text-gray-600 text-sm sm:text-base">{BRAND.address}</p>
                  </div>
                  
                  {/* Google Maps для Emmendingen */}
                  <div className="relative h-64 sm:h-96 w-full rounded-b-3xl overflow-hidden">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2640.1234567890123!2d7.851234567890123!3d48.12345678901234!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDjCsDA3JzM0LjQiTiA3wrA1MScwNC40IkU!5e0!3m2!1sde!2sde!4v1234567890123"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Офис гипнотерапии в Германии"
                    ></iframe>
                  </div>
                </motion.div>

                {/* Информация о местоположении */}
                <motion.div variants={item} className="bg-gradient-to-br from-rose-600 to-rose-700 rounded-2xl p-4 sm:p-6 border border-rose-500">
                  <h3 className="font-semibold text-white mb-3 sm:mb-4 text-sm sm:text-base">{t('contact.directions.title')}</h3>
                  <div className="space-y-2 sm:space-y-3 text-xs sm:text-sm text-rose-100">
                    <p><strong className="text-white">{t('contact.directions.car')}:</strong> {t('contact.directions.car_description')}</p>
                    <p><strong className="text-white">{t('contact.directions.public')}:</strong> {t('contact.directions.public_description')}</p>
                    <p><strong className="text-white">{t('contact.directions.bike')}:</strong> {t('contact.directions.bike_description')}</p>
                  </div>
                </motion.div>

                {/* Кнопка "Проложить маршрут" */}
                <motion.div variants={item}>
                  <a
                    href={`https://www.google.com/maps/dir//${encodeURIComponent(BRAND.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full bg-rose-600 hover:bg-rose-700 text-white py-3 sm:py-4 px-4 sm:px-6 rounded-2xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 sm:gap-3 text-sm sm:text-base"
                  >
                    <MapPin className="h-4 w-4 sm:h-5 sm:w-5" />
                    {t('contact.route_button')}
                  </a>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Гарантии */}
        <section className="py-16 sm:py-20 bg-black">
          <div className="container-spacious">
            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="text-center mb-12 sm:mb-16"
            >
              <motion.h2 variants={item} className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6 text-white">
                {t('contact.guarantees.title')}
              </motion.h2>
              <motion.p variants={item} className="text-gray-300 max-w-3xl mx-auto text-sm sm:text-base">
                {t('contact.guarantees.subtitle')}
              </motion.p>
            </motion.div>

            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8"
            >
              {guarantees.map((guarantee, index) => (
                <motion.div
                  key={index}
                  variants={item}
                  className="bg-white/5 border border-white/10 rounded-2xl p-6 sm:p-8 hover:bg-white/15 transition-all hover:scale-105 text-center"
                >
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-rose-100 rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6">
                    <guarantee.icon className="w-6 h-6 sm:w-8 sm:h-8 text-rose-600" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4 text-white">{guarantee.title}</h3>
                  <p className="text-gray-200 text-sm sm:text-base">{guarantee.description}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Социальные сети */}
        <section className="py-16 sm:py-20 bg-white">
          <div className="container-spacious">
            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="text-center mb-12 sm:mb-16"
            >
              <motion.h2 variants={item} className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6 text-dark-900">
                {t('contact.updates.title')}
              </motion.h2>
              <motion.p variants={item} className="text-base sm:text-lg max-w-3xl mx-auto text-gray-600">
                {t('contact.updates.subtitle')}
              </motion.p>
            </motion.div>

            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="flex justify-center gap-4 sm:gap-6 mb-12 sm:mb-16"
            >
              {socialLinks.map((social, index) => (
                <motion.a
                  key={index}
                  variants={item}
                  href={social.url}
                  className={`w-12 h-12 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center transition-all hover:scale-110 ${social.color} bg-gray-100 hover:bg-gray-200`}
                >
                  <social.icon className="w-6 h-6 sm:w-8 sm:h-8" />
                </motion.a>
              ))}
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
